import type { View } from "@-/com.npmjs.react-native";
import {
  type SharedValue,
  useAnimatedStyle,
  useReducedMotion,
} from "@-/com.npmjs.react-native-reanimated";
import type { HanReward } from "@-/dev.improve.haniverse.lib.han-currency";
import { createContext, use, useCallback, useEffect, useState } from "react";
import {
  hanCoinDepositWalletDisplay,
  type WindowPoint,
  type WindowRect,
} from "#han-coin-deposit-plan";

/**
 * Places coins can burst out of. Each registers its view with `useHanCoinDepositSource`: today's
 * column of the 红包 strip (`daily-reward`), or the map home's Continue Learning button, which
 * opens it too (`continue-learning`).
 */
export type HanCoinDepositSourceName = "continue-learning" | "daily-reward";

/**
 * Where coins land. The drawer button (`drawer`) always can. While the map's sheet is on screen its
 * wallet takes the deposit instead: Han lands in its Han balance (`wallet-han`) and HanMiles in its
 * HanMiles balance (`wallet-han-miles`), which count up themselves.
 */
export type HanCoinDepositTargetName = "drawer" | "wallet-han" | "wallet-han-miles";

/** The balances a deposit lands in, read from the database when it starts. */
export type HanCoinDepositBalances = { han: number; hanMiles: number };

/**
 * What the deposit brings (missing fields are 0). Han flies into the drawer button (or the map
 * sheet's wallet) as coins, the coin count growing with it; everything else (dragons, HanMiles, a
 * review bonus) is shown beside the amount, and a deposit without Han shows only that.
 */
export type HanCoinDepositRequest = Partial<HanReward> & {
  /**
   * Where the coins burst out: a registered source, measured now. An unmounted source (or `null`)
   * bursts from the middle of the screen instead.
   */
  source: HanCoinDepositSourceName | null;
};

export type ActiveHanCoinDeposit = HanReward & {
  /**
   * The wallet once this deposit has landed, read from the database when it starts; the counter
   * counts up to it. `null` until read, or when the read failed.
   */
  balanceAfter: HanCoinDepositBalances | null;
  id: number;
  /** The Han of the coins that have landed so far. */
  landedHan: number;
  /**
   * Where it lands: the drawer button, whose counter pops out beside it, or the map sheet's wallet,
   * whose balances count up in place (`useHanCoinDepositWalletDisplay`).
   */
  landsIn: "drawer" | "wallet";
  source: WindowRect;
  target: WindowPoint & { size: number };
};

export type HanCoinDepositContextValue = {
  /** Banks a reward with the coin animation. Call it once the reward is committed. */
  deposit: (request: HanCoinDepositRequest) => void;
  registerSource: (name: HanCoinDepositSourceName, view: View) => () => void;
  registerTarget: (name: HanCoinDepositTargetName, view: View) => () => void;
  /** 0 at rest; each landing kicks its target's to 1 and springs it back. Drives the bump. */
  targetPulses: Record<HanCoinDepositTargetName, SharedValue<number>> | null;
};

/** Without a provider (tests, stories) deposits are silently skipped: the Han is banked anyway. */
export const HanCoinDepositContext = createContext<HanCoinDepositContextValue>({
  deposit: () => {},
  registerSource: () => () => {},
  registerTarget: () => () => {},
  targetPulses: null,
});

/**
 * The deposits in flight, read only by the overlay and the map sheet's wallet, so a deposit
 * re-renders nothing else.
 */
export const HanCoinDepositsContext = createContext<{
  deposits: readonly ActiveHanCoinDeposit[];
  /** The app's mute: deposits still tick and bump, but clink silently. */
  isMuted: boolean;
  /** A coin of `id` landed, bringing `han`. */
  landHan: (id: number, han: number) => void;
  removeDeposit: (id: number) => void;
}>({ deposits: [], isMuted: true, landHan: () => {}, removeDeposit: () => {} });

export function useHanCoinDeposit(): HanCoinDepositContextValue["deposit"] {
  return use(HanCoinDepositContext).deposit;
}

/** A ref callback for a view that is registered while it is mounted and `enabled`. */
function useRegisteredViewRef(register: (view: View) => () => void, enabled = true) {
  const [view, setView] = useState<View | null>(null);
  useEffect(
    () => (enabled && view !== null ? register(view) : undefined),
    [enabled, register, view],
  );
  return setView;
}

/** A ref for the view coins burst out of when a deposit names `name` as its source. */
export function useHanCoinDepositSource(name: HanCoinDepositSourceName) {
  const { registerSource } = use(HanCoinDepositContext);
  return useRegisteredViewRef(
    useCallback((view: View) => registerSource(name, view), [name, registerSource]),
  );
}

/**
 * A view coins drop into: a ref to measure it by, and the bump each landing gives it. Put the ref
 * on a plain view and the style on an animated one inside it, so the bump never changes the
 * measured frame. The drawer button is always a target; the map sheet's wallet is one only while
 * `enabled` (the map is on screen), so a deposit made as the map leaves lands in the drawer.
 */
export function useHanCoinDepositTarget(
  name: HanCoinDepositTargetName = "drawer",
  { enabled = true }: { enabled?: boolean } = {},
) {
  const { registerTarget, targetPulses } = use(HanCoinDepositContext);
  const ref = useRegisteredViewRef(
    useCallback((view: View) => registerTarget(name, view), [name, registerTarget]),
    enabled,
  );
  const targetPulse = targetPulses?.[name] ?? null;
  const pulseStyle = useAnimatedStyle(() => {
    const pulse = targetPulse?.get() ?? 0;
    return { transform: [{ scale: 1 + pulse * 0.18 }, { rotateZ: `${pulse * -6}deg` }] };
  });
  return { pulseStyle, ref };
}

/**
 * The balances the map sheet's wallet shows: `wallet`, except while a deposit lands in it. Its Han
 * then counts up from the balance before the deposit as each coin lands, and its HanMiles move to
 * the new balance once the deposit is banked.
 */
export function useHanCoinDepositWalletDisplay(wallet: HanCoinDepositBalances) {
  const { deposits } = use(HanCoinDepositsContext);
  const isReducedMotion = useReducedMotion();
  // The latest one landing in the wallet (`findLast` is past this package's target library).
  let deposit: ActiveHanCoinDeposit | undefined;
  for (const entry of deposits) {
    if (entry.landsIn === "wallet") {
      deposit = entry;
    }
  }
  return hanCoinDepositWalletDisplay(wallet, deposit, isReducedMotion);
}
